import { DataTable, useDataTableUsingUrl } from '@juvare/common-data-table-ui/DataTable';
import { Slideout } from '@juvare/common-web-ui/Slideout';
import { useUserState } from '@juvare/contact-management/ui/hooks';
import AddIcon from '@mui/icons-material/Add';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { FormControlLabel, Switch, useMediaQuery } from '@mui/material';
import React from 'react';
import { useParams, useSearchParams } from 'react-router';
import { allowWrite, getDefaultPermissions } from '../../../backend/features/permissions';
import { showCreateDirectoryDialog, showHistoryDialog, showUploadMultipleFiles } from '../../components';
import { useDocumentLibraryStorage, useDocumentLibraryTranslations, useFileTable } from '../../hooks';
import { useManagePermission } from '../../hooks/useManagePermission';
import { DocumentLibraryContext } from '../../providers';
import { FileManagerSlideout } from './components';
export function FileManager(props) {
    var _a, _b, _c, _d, _e;
    const storage = useDocumentLibraryStorage();
    const { t } = useDocumentLibraryTranslations();
    const userState = useUserState();
    const downXsScreen = useMediaQuery((theme) => theme.breakpoints.down('sm'));
    const { handleCurrentDirectoryId, currentParentPermission } = React.useContext(DocumentLibraryContext);
    const [searchParams, setSearchParams] = useSearchParams();
    const params = useParams();
    const currentDirectoryId = (_a = params.currentDirectoryId) !== null && _a !== void 0 ? _a : props.currentDirectoryId;
    const { showManagePermissionDialog } = useManagePermission();
    const ownerId = searchParams.get('ownerId') || undefined;
    const watching = searchParams.get('watching') === 'true';
    const staticFilterData = {
        ownerId: ownerId,
        watching: watching,
        currentUserId: userState.id,
    };
    React.useEffect(() => {
        var _a;
        handleCurrentDirectoryId((_a = params.currentDirectoryId) !== null && _a !== void 0 ? _a : props.currentDirectoryId);
    }, [params.currentDirectoryId, props.currentDirectoryId, handleCurrentDirectoryId]);
    const updateSearchParams = (param, value, isChecked) => {
        const newSearchParams = new URLSearchParams(searchParams);
        if (isChecked && value !== undefined) {
            newSearchParams.set(param, value);
        }
        else {
            newSearchParams.delete(param);
        }
        newSearchParams.set('page', '0');
        setSearchParams(newSearchParams, { replace: true });
    };
    const { dataTableProps, reload, clearActiveRow } = useFileTable({
        useDataTableFunction: useDataTableUsingUrl,
        defaultPermissions: getDefaultPermissions(),
        currentDirectoryId: currentDirectoryId,
        navigate: props.navigate,
        onManagePermissions: showManagePermissionDialog,
        staticFilterData: staticFilterData,
        customRowMenuItems: props.customRowMenuItems,
    });
    let allowCreate = false;
    if (currentParentPermission) {
        allowCreate = currentParentPermission === null || currentParentPermission === void 0 ? void 0 : currentParentPermission.includes('write');
    }
    else if (userState) {
        // if this is root folder call allowWrite callback to check if we can write at root level
        allowCreate = allowWrite({
            userState: userState,
            ownerId: undefined,
            permissions: getDefaultPermissions(),
            rootLevel: true,
        });
    }
    const handleCreateFolder = () => {
        if (userState.id) {
            showCreateDirectoryDialog({
                t,
                storage: storage,
                ownerId: userState.id,
                mobileScreen: downXsScreen,
                parentId: currentDirectoryId,
                onSuccess: () => reload(),
            });
        }
    };
    const reloadAfterFileChange = (close) => {
        if (close) {
            clearActiveRow();
        }
        reload();
    };
    const handleShowHistory = (entityId, fileName, currentVersionId) => {
        showHistoryDialog({ entityId, fileName, currentVersionId, onBackToFiles: () => reload() });
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(DataTable, Object.assign({}, dataTableProps, { title: (_b = props.fileTableTitle) !== null && _b !== void 0 ? _b : t('documentPanel.active'), filteredRowCount: (_c = dataTableProps.filteredRowCount) !== null && _c !== void 0 ? _c : 0, customHeaderElement: userState.id ? (React.createElement(React.Fragment, null,
                React.createElement(FormControlLabel, { label: t('documentPanel.myFilesOnly'), control: React.createElement(Switch, { checked: Boolean(ownerId), onChange: (event) => updateSearchParams('ownerId', userState.id, event.target.checked) }) }),
                React.createElement(FormControlLabel, { label: t('documentPanel.watchedFilesOnly'), control: React.createElement(Switch, { checked: watching, onChange: (event) => updateSearchParams('watching', 'true', event.target.checked) }) }))) : null, mainAction: ((_d = props.showCreateFolderButton) !== null && _d !== void 0 ? _d : allowCreate)
                ? {
                    icon: React.createElement(AddIcon, null),
                    label: t('documentPanel.createFolder'),
                    'data-test': 'create-folder-button',
                    onClick: handleCreateFolder,
                }
                : undefined, secondaryAction: ((_e = props.showUploadFileButton) !== null && _e !== void 0 ? _e : allowCreate)
                ? {
                    icon: React.createElement(FileUploadOutlinedIcon, null),
                    label: t('documentPanel.uploadFile'),
                    'data-test': 'upload-file-button',
                    onClick: () => showUploadMultipleFiles({
                        ownerId: userState.id,
                        parentId: currentDirectoryId,
                        onDone: () => reload(),
                    }),
                }
                : undefined })),
        React.createElement(Slideout, { open: !!dataTableProps.activeRowId, onClose: () => {
                clearActiveRow();
            } }, dataTableProps.activeRowId && (React.createElement(FileManagerSlideout, { fileId: dataTableProps.activeRowId, customSlideoutMenuItems: props.customSlideoutMenuItems, onManagePermissions: showManagePermissionDialog, onSetShowHistory: handleShowHistory, onReload: reloadAfterFileChange, onEditDetails: () => {
                reload();
            } })))));
}
